import React, { Component } from 'react';
import { Table, Glyphicon } from 'react-bootstrap';
import classnames from 'classnames';
import { withTranslation } from 'react-i18next';
import { titleize } from 'underscore.string';
import { getEquipmentTypesForShipmentTypes } from 'constants/EquipmentTypes';
import UserWebAPIUtils from 'utils/UserWebAPIUtils';
import {
  formattedLastSeen,
  formatPlatformLabel,
} from 'utils/localization/utils';
import { formatDate } from 'utils/formatters/Date';
import UserActionCreators from '../../../actions/UserActionCreators';
import AddDriverFormActionCreators from '../../../actions/drivers/AddDriverFormActionCreators';
import DriverActionsCreators from '../../../actions/DriverActionCreators';

import AddDriverFormStore from '../../../stores/drivers/AddDriverFormStore';
import SessionStore from '../../../stores/SessionStore';
import DriverStore from '../../../stores/DriverStore';
import DriverJobsModal from './DriverJobsModal';
import AddDriverModal from '../user/carrier/add-driver-modal';
import { Avatar } from '../user/Avatar';
import SHIPMENT_TYPES from 'constants/ShipmentTypes';

const shipmentTypes = SHIPMENT_TYPES.map((type) => type.value);

function getHandleAuthorizedChange(id) {
  return (e) => {
    const carrier = SessionStore.getUser();
    DriverActionsCreators.toggleDriverAuthorization(
      id,
      e.target.checked,
      carrier?._id,
    );
  };
}

function getHandleMultiplePickupsChange(id) {
  return (e) => {
    const carrier = SessionStore.getUser();
    const multiplePickupAllowed = e.target.checked;
    DriverActionsCreators.toggleMultiplePickup(
      id,
      multiplePickupAllowed,
      carrier?._id,
    );
  };
}

function getHandleReceiveOffersChange(id) {
  return (e) => {
    const carrier = SessionStore.getUser();
    const receiveOffers = e.target.checked;
    DriverActionsCreators.toggleReceiveOffers(id, receiveOffers, carrier?._id);
  };
}

function getHandleCanSeePayout(id) {
  return (e) => {
    const carrier = SessionStore.getUser();
    DriverActionsCreators.toggleCanSeePayout(
      id,
      !e.target.checked,
      carrier?._id,
    );
  };
}

function fetchDrivers() {
  const carrier = SessionStore.getUser();
  if (carrier) {
    DriverActionsCreators.getAllDriversForCarrier(carrier._id);
  }
}

class DriverList extends Component {
  constructor(props) {
    super(props);
    this.state = {
      drivers: [],
      loading: true,
      jobModal: {
        isOpen: false,
        jobs: [],
        type: '',
      },
      addDriverModal: {
        formState: {
          error: {},
          show: false,
          isSuccess: false,
        },
      },
      savingDriver: false,
    };
  }

  componentDidMount() {
    fetchDrivers();
    DriverStore.addChangeListener(this.handleDriverChange);
    AddDriverFormStore.addChangeListener(this.handleAddDriverFormChange);
  }

  componentWillUnmount() {
    DriverStore.removeChangeListener(this.handleDriverChange);
    AddDriverFormStore.removeChangeListener(this.handleAddDriverFormChange);
  }

  handleAddDriverFormChange = () => {
    const formState = AddDriverFormStore.getFormState();

    this.setState({
      addDriverModal: {
        formState,
      },
      savingDriver: false,
    });
    if (formState.isSuccess) {
      fetchDrivers();
      setTimeout(() => {
        AddDriverFormActionCreators.hideForm();
      }, 3000);
    } else if (formState.submittable) {
      const carrier = SessionStore.getUser();
      if (carrier) {
        UserActionCreators.addDriver({
          ...formState.driver,
          carrier: carrier._id,
        });
      }
    }
  };

  handleCloseAddDriverModal = () => {
    AddDriverFormActionCreators.hideForm();
  };

  handleShowAddDriverModal = () => {
    AddDriverFormActionCreators.showForm();
  };

  handleCloseModal = () => {
    AddDriverFormActionCreators.hideForm();
    this.setState({
      jobModal: {
        isOpen: false,
        jobs: [],
        type: '',
        name: '',
      },
    });
  };

  handleAddDriver = (driver) => {
    this.setState({ savingDriver: true }, () =>
      AddDriverFormActionCreators.requestValidateNewDriver(driver),
    );
  };

  handleDriverChange = () => {
    const drivers = DriverStore.getAllDrivers();
    this.setState({
      drivers,
      loading: false,
    });
  };

  handleSelectAllAuthorized = (e) => {
    const carrier = SessionStore.getUser();
    const { drivers } = this.state;
    const driverIds = drivers.map((driver) => driver._id);
    DriverActionsCreators.toggleDriverAuthorization(
      driverIds,
      e.target.checked,
      carrier?._id,
    );
  };

  handleSelectAllMultiplePickups = (e) => {
    const carrier = SessionStore.getUser();
    const { drivers } = this.state;
    const multiplePickupAllowed = e.target.checked;
    const driverIds = drivers.map((driver) => driver._id);
    DriverActionsCreators.toggleMultiplePickup(
      driverIds,
      multiplePickupAllowed,
      carrier?._id,
    );
  };

  handleSelectAllReceiveOffers = (e) => {
    const carrier = SessionStore.getUser();
    const { drivers } = this.state;
    const receiveOffers = e.target.checked;
    const driverIds = drivers.map((driver) => driver._id);
    DriverActionsCreators.toggleReceiveOffers(
      driverIds,
      receiveOffers,
      carrier?._id,
    );
  };

  handleSelectAllHidePrices = (e) => {
    const { drivers } = this.state;
    const carrier = SessionStore.getUser();
    const driverIds = drivers.map((driver) => driver._id);
    DriverActionsCreators.toggleCanSeePayout(
      driverIds,
      !e.target.checked,
      carrier?._id,
    );
  };

  handleEquipmentTypeSave = async (e, driverId) => {
    const label = e.target.value === 'Select' ? '' : e.target.value;

    try {
      const update = { equipment_class: { label } };
      const response = await UserWebAPIUtils.updateDriverEquipment(
        driverId,
        update,
      );
      if (response.success) {
        fetchDrivers();
      }
    } catch (e) {
      console.log('Equipment save error: ', e);
    }
  };

  renderEquipmentTypes = (driver) => {
    const equipmentClass = driver.equipment_class;
    const equipment = equipmentClass?.label || '';
    const { t } = this.props;
    return (
      <div>
        <select
          className="w-56 form-select"
          value={equipment}
          onSelect={(e) => this.handleEquipmentTypeSave(e, driver._id)}
          onChange={(e) => this.handleEquipmentTypeSave(e, driver._id)}
        >
          <option key="select" value="select">
            {t('SELECT')}
          </option>
          {getEquipmentTypesForShipmentTypes(shipmentTypes).map((item) => (
            <option key={item.label} value={item.label}>
              {t(item.label)}
            </option>
          ))}
        </select>
      </div>
    );
  };

  renderDrivers() {
    const { drivers, loading } = this.state;
    const { t } = this.props;
    if (loading) {
      return (
        <tr>
          <td colSpan="4">{t('LOADING')}</td>
        </tr>
      );
    }

    if (!drivers || drivers.length === 0) {
      return (
        <tr>
          <td colSpan="4">{t('NO_DRIVER_REGISTERED')}</td>
        </tr>
      );
    }

    return drivers.map((driver) => {
      const glyph = driver.available ? 'ok-circle' : 'ban-circle';
      const glyphClassName = classnames({
        'text-green': glyph === 'ok-circle',
        'text-red': glyph === 'ban-circle',
      });
      const driverLink = `/drivers/${driver._id}/manage`;
      const createdAt = driver?.created_at
        ? formatDate(driver?.created_at, {
            dateFormat: DEFAULT_DATE_FORMAT,
            timeFormat: DEFAULT_TIME_FORMAT,
            timezone: DEFAULT_TIMEZONE,
            showTimezone: false,
            t,
          })
        : '';
      return (
        <tr key={driver._id}>
          <td>
            <Avatar
              key={driver._id}
              userId={driver._id}
              altName={driver.fullName}
              className="inline-block w-8 h-8 mr-2 rounded-full"
            />
            <div className="caption">
              <a href={driverLink}>{driver.fullName}</a>
            </div>
          </td>
          <td>
            <span>{driver.username}</span>
          </td>
          <td>
            <span>{createdAt}</span>
          </td>
          <td>
            <span>{formattedLastSeen(driver.lastSeen)}</span>
          </td>
          <td>
            <span>{formatPlatformLabel(driver.platformLabel.toString())}</span>
          </td>
          <td>
            <span>{this.renderEquipmentTypes(driver)}</span>
          </td>
          <td>
            <Glyphicon glyph={glyph} className={glyphClassName} />
          </td>
          <td>
            <input
              type="checkbox"
              checked={driver.authorized}
              onChange={getHandleAuthorizedChange(driver._id)}
            />
          </td>
          <td>
            <input
              type="checkbox"
              checked={driver.can_accept_offers_before_pickup}
              onChange={getHandleMultiplePickupsChange(driver._id)}
            />
          </td>
          <td>
            <input
              type="checkbox"
              checked={driver.receive_offers}
              onChange={getHandleReceiveOffersChange(driver._id)}
            />
          </td>
          <td>
            <input
              type="checkbox"
              checked={!driver.can_see_payout}
              onChange={getHandleCanSeePayout(driver._id)}
            />
          </td>
        </tr>
      );
    });
  }

  render() {
    const { t } = this.props;
    const { drivers } = this.state;
    const hasDrivers = drivers && drivers.length > 0;
    const allAuthorized = hasDrivers && drivers.every((d) => d.authorized);
    const allMultiplePickups =
      hasDrivers && drivers.every((d) => d.can_accept_offers_before_pickup);
    const allReceiveOffers =
      hasDrivers && drivers.every((d) => d.receive_offers);
    const allHidePrices = hasDrivers && drivers.every((d) => !d.can_see_payout);
    return (
      <div className="container driver-list">
        <div className="driver-list-actions">
          <button
            className="button button-green"
            onClick={this.handleShowAddDriverModal}
          >
            <Glyphicon glyph="plus" />
            {t('ADD_DRIVER')}
          </button>
        </div>
        <Table striped hover className="driver-list-table">
          <thead>
            <tr className="text-grey-darkest">
              <th>{titleize(t('DRIVER', { count: 1 }))}</th>
              <th>{titleize(t('USERNAME', { count: 1 }))}</th>
              <th>{t('CREATED_DATE')}</th>
              <th>{t('LAST_SEEN')}</th>
              <th>{titleize(t('PLATFORM', { count: 1 }))}</th>
              <th>{t('EQUIPMENT_CLASS')}</th>
              <th>{titleize(t('AVAILABLE', { count: 1 }))}?</th>
              <th>
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="selectAllAuthorized"
                    aria-label="selectAllAuthorized"
                    checked={allAuthorized}
                    onChange={this.handleSelectAllAuthorized}
                  />
                  {titleize(t('AUTHORIZED'))}?
                </span>
              </th>
              <th>
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="selectAllMultiplePickups"
                    aria-label="selectAllMultiplePickups"
                    checked={allMultiplePickups}
                    onChange={this.handleSelectAllMultiplePickups}
                  />
                  {t('MULTIPLE_PICKUPS')}?
                </span>
              </th>
              <th>
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="selectAllReceiveOffers"
                    aria-label="selectAllReceiveOffers"
                    checked={allReceiveOffers}
                    onChange={this.handleSelectAllReceiveOffers}
                  />
                  {t('RECEIVE_OFFERS')}?
                </span>
              </th>
              <th>
                <span className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    name="selectAllHidePrices"
                    aria-label="selectAllHidePrices"
                    checked={allHidePrices}
                    onChange={this.handleSelectAllHidePrices}
                  />
                  {t('HIDE_PRICES')}?
                </span>
              </th>
            </tr>
          </thead>
          <tbody>{this.renderDrivers()}</tbody>
        </Table>
        {this.state.drivers && this.state.drivers.length > 0 && (
          <div className="py-2 italic">
            {t('SHOWING_DRIVERS_LENGTH', {
              drivers_length: this.state.drivers.length,
            })}
          </div>
        )}
        <div>
          {[
            <DriverJobsModal
              {...this.state.jobModal}
              key="jobs-modal"
              onClose={this.handleCloseModal}
            />,
            <AddDriverModal
              key="add-driver-modal"
              onHide={this.handleCloseAddDriverModal}
              onSave={this.handleAddDriver}
              formState={this.state.addDriverModal.formState}
              savingDriver={this.state.savingDriver}
            />,
          ]}
        </div>
      </div>
    );
  }
}

export default withTranslation()(DriverList);
